// The picture, as both sides draw it.
//
// One renderer for the page being played: the background with its animation,
// the text in its authored box, and the tap zones over it. The storefront
// player shows it to a reader and the editor shows it to whoever is making
// the book — the same code, so what an author sees is what a reader gets.
import React, { useEffect, useRef, useState } from "react"
import Background from "./Background"
import Subtitles from "./Subtitles"
import TriggerAreas from "./TriggerAreas"
import { getPageImage } from "./layout"

/** The box's size in pixels, kept current as it is resized. */
export function useStageViewport(ref) {
  const [viewport, setViewport] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const element = ref.current
    if (!element) return
    const measure = () => setViewport({ width: element.clientWidth, height: element.clientHeight })
    measure()
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure)
      return () => window.removeEventListener("resize", measure)
    }
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])

  return viewport
}

/**
 * `triggers` and `subtitles` are the editor's switches: while authoring, the
 * tap zones are in the way and the text is sometimes what is being moved.
 */
export default function Stage({ product, page, engine, state, className = "", subtitles = true, triggers = true, children }) {
  const ref = useRef(null)
  const viewport = useStageViewport(ref)
  const hasImage = !!getPageImage(page)
  const background = product?.bookSettings?.backgroundColor || "#000"

  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`} style={{ background }} data-testid="player-stage">
      {hasImage && viewport.width > 0 && <Background page={page} viewport={viewport} engine={engine} state={state} />}
      {hasImage && viewport.width > 0 && subtitles && <Subtitles page={page} viewport={viewport} visible />}
      {hasImage && viewport.width > 0 && triggers && (
        <TriggerAreas page={page} viewport={viewport} engine={engine} pageCount={(product?.pages || []).length} />
      )}
      {children}
    </div>
  )
}
